/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect } from 'react';
import { FormControlLabel, makeStyles, Radio, RadioGroup, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { uiSchemaProperties } from 'shared/enums/uiSchemaProperties';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    marginBotttom: '2rem',
  },
  radioButton: ({ color }) => ({
    backgroundColor: 'transparent',
    color: `${color}`,
    '&:hover': {
      color: `${color}`,
    },
    '& > span > div > svg': {
      '&:last-child': {
        transform: 'scale(2.5)',
      },
    },
  }),
  checked: ({ color }) => ({
    color: `${color} !important`,
    '& > span > div > svg': {
      '&:first-child': {
        color: 'black !important',
        transform: 'scale(1.5) !important',
      },
      '&:last-child': {
        transform: 'scale(2) !important',
      },
    },
  }),
}));

const ColoredRadioButton = ({ color, testId, value }) => {
  const classes = useStyles({ color });
  return <Radio data-testid={testId} value={value} classes={{ root: classes.radioButton, checked: classes.checked }} />;
};

/**
 * Component for selecting an accent color from a set of predefined colors
 * @param {*} options Object containing the enumOptions - an array of the predefined colors often defined in the jsonschema
 * @param onChange callback function to trigger when the selection changes
 * @param uiSchema
 * @param id unique identifier
 * @returns
 */
export default function AccentColorPicker({ options, onChange, uiSchema, id, value }) {
  const { t } = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    onChange(value || options?.enumOptions?.[0].value);
  }, []);

  const onColorChange = (evt) => {
    onChange(evt.target.value);
  };

  return (
    <>
      <div className={classes.header}>
        <Typography component="h2" variant="h2" gutterBottom>
          {uiSchema[uiSchemaProperties.UI_TITLE]}
        </Typography>

        <Typography component="p" variant="subtitle1" gutterBottom className={classes.description}>
          {uiSchema[uiSchemaProperties.UI_DEFINITION]}
        </Typography>
      </div>

      <RadioGroup
        id={id || 'accent-colour-picker'}
        aria-label={t('accent.color')}
        value={value || options?.enumOptions?.[0].value || ''}
        onChange={onColorChange}
        className={classes.container}
      >
        {options?.enumOptions?.map(option => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<ColoredRadioButton testId={option.value} value={option.value} color={option.value} />}
            aria-checked={value === option.value}
            aria-label={`${option.value}`}
          />
        ))}
      </RadioGroup>
    </>
  );
}
